import { useState, FormEvent } from 'react';

export default function ContactFooter() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [formValues, setFormValues] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });

  const handleContactSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const honeypot = form.querySelector('[name="company_alt"]') as HTMLInputElement;
    if (honeypot && honeypot.value.trim() !== '') {
      setFormStatus('success');
      setFormValues({ name: '', email: '', phone: '', company: '', message: '' });
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const submitFormData = new FormData(form);
      const response = await fetch('', {
        method: 'POST',
        body: submitFormData,
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormValues({ name: '', email: '', phone: '', company: '', message: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || 'Something went wrong. Please try again.';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('Network error. Please try again.');
      setFormStatus('error');
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-primary-900">
      {/* Main footer content */}
      <div className="w-full px-4 md:px-8 py-10 md:py-14">
        <div className="max-w-4xl mx-auto">
          {/* Headings */}
          <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl text-white text-center tracking-[0.2em] mb-1">
            CONTACT &amp; LOCATION
          </h2>
          <p className="text-[10px] md:text-xs text-white/70 text-center tracking-[0.3em] uppercase mb-6 md:mb-8 font-medium">
            HOW CAN WE HELP YOU?
          </p>

          {formStatus === 'success' ? (
            <p className="text-sm text-white/70 text-center">Thanks for submitting!</p>
          ) : (
            <form
              data-readdy-form
              id="contact-form"
              onSubmit={handleContactSubmit}
            >
              {/* Honeypot */}
              <input
                type="text"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="honeypot-field"
              />

              {/* 2-column form layout matching original */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                {/* Left column - inputs */}
                <div className="space-y-3">
                  <input
                    type="text"
                    name="name"
                    value={formValues.name}
                    onChange={(e) => setFormValues({ ...formValues, name: e.target.value })}
                    placeholder="Name"
                    required
                    maxLength={100}
                    className="w-full px-3 py-2.5 text-[13px] bg-white/10 border border-primary-700 text-white placeholder:text-white/50 focus:outline-none focus:border-white/40"
                  />
                  <input
                    type="email"
                    name="email"
                    value={formValues.email}
                    onChange={(e) => setFormValues({ ...formValues, email: e.target.value })}
                    placeholder="Email"
                    required
                    maxLength={250}
                    className="w-full px-3 py-2.5 text-[13px] bg-white/10 border border-primary-700 text-white placeholder:text-white/50 focus:outline-none focus:border-white/40"
                  />
                  <input
                    type="tel"
                    name="phone"
                    value={formValues.phone}
                    onChange={(e) => setFormValues({ ...formValues, phone: e.target.value })}
                    placeholder="Phone"
                    maxLength={50}
                    className="w-full px-3 py-2.5 text-[13px] bg-white/10 border border-primary-700 text-white placeholder:text-white/50 focus:outline-none focus:border-white/40"
                  />
                  <input
                    type="text"
                    name="company"
                    value={formValues.company}
                    onChange={(e) => setFormValues({ ...formValues, company: e.target.value })}
                    placeholder="Company"
                    maxLength={100}
                    className="w-full px-3 py-2.5 text-[13px] bg-white/10 border border-primary-700 text-white placeholder:text-white/50 focus:outline-none focus:border-white/40"
                  />
                </div>

                {/* Right column - message + submit */}
                <div className="flex flex-col gap-3">
                  <textarea
                    name="message"
                    value={formValues.message}
                    onChange={(e) => setFormValues({ ...formValues, message: e.target.value })}
                    placeholder="Type your message here..."
                    required
                    maxLength={500}
                    className="flex-1 w-full px-3 py-2.5 text-[13px] bg-white/10 border border-primary-700 text-white placeholder:text-white/50 focus:outline-none focus:border-white/40 resize-none min-h-[120px]"
                  />
                  <div className="text-center sm:text-left">
                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="px-8 py-2.5 text-[11px] font-semibold tracking-[0.2em] uppercase text-white bg-primary-500 hover:bg-primary-400 transition-colors disabled:opacity-60 whitespace-nowrap"
                    >
                      {formStatus === 'submitting' ? 'Submitting...' : 'Submit'}
                    </button>
                  </div>
                </div>
              </div>
              {formStatus === 'error' && formError && (
                <p className="text-xs text-red-400 text-center">{formError}</p>
              )}
            </form>
          )}

          {/* Address info */}
          <div className="mt-8 text-center space-y-1">
            <p className="text-[11px] text-white/70 tracking-wide">1007 High St, Fall River, MA 02720</p>
            <p className="text-[11px] text-white/70 tracking-wide">(380) 213-8122</p>
            <p className="text-[11px] text-white/70 tracking-wide">support@kindseafoods.com</p>
            <p className="text-[11px] text-white/70 tracking-wide">Monday - Friday 6:00 AM - 2:30 PM</p>
            <p className="text-[11px] text-white/70 tracking-wide">Saturday 6:00 AM - 9:30 AM</p>
            <p className="text-[11px] text-white/70 tracking-wide">Closed on Sunday</p>
          </div>

          {/* Map */}
          <div className="mt-8 w-full aspect-video overflow-hidden bg-primary-800">
            <iframe
              title="Finest Kind Seafood LLC Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2981.5!2d-71.1305!3d41.7015!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2s1007%20High%20St%2C%20Fall%20River%2C%20MA%2002720!5e0!3m2!1sen!2sus!4v1600000000000!5m2!1sen!2sus"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </div>

      {/* Go to top and copyright */}
      <div className="border-t border-primary-800 py-6 text-center">
        <button
          onClick={scrollToTop}
          className="flex flex-col items-center gap-1 text-white/60 hover:text-white transition-colors mx-auto mb-4"
        >
          <i className="ri-arrow-up-s-line text-xl" />
          <span className="text-[10px] tracking-[0.25em] uppercase">GO TO TOP</span>
        </button>
        <p className="text-[10px] text-white/50 tracking-wide">
          &copy; 2020 by Finest Kind Seafood LLC.
        </p>
      </div>
    </footer>
  );
}